import { ref } from 'vue'
import { makeItReactive } from '../front/$frameworkUtils/$frameworkUtils'
import { singletonInstanceSummoner } from './singletonInstanceSummoner'

/**
 * # $core.$uiState
 * `$core.$uiState` 画面 (Vueコンポーネント) 全体を通じたUIの状態 (Reactive) を保持するサービス
 *
 * Vueコンポーネント側で、`$core.$uiState[propName]` を参照 もしくは watch することで、画面表示を切り替えたり、アクションを実行するhookとすることが可能。 (オブジェクト全体がVue Reactive になっている)
 *
 * ```ts
 * // Vueコンポーネント内で、画面上でデータ更新があった際に メソッドを発火する例
 * export default {
 *   ...,
 *   watch: {
 *     // ユーザがデータを更新 or 削除したら、fetchData() を発火
 *     '$core.$uiState.userLatestStoreMethodActionTime'() {
 *       this.fetchData()
 *     },
 *   }
 * }
 * ```
 *
 * ```vue
 * // Vueコンポーネント内で、現在表示中のアプリケーション定義名を表示
 * <template>
 *   <div>{{ $core.$uiState.appName }}</div>
 * </template>
 * ```
 *
 * @category $core/20.ui/204.uiState
 */
export class UiState {
  /**
   * 一覧画面で表示する件数を保持
   * @hidden
   */
  hitsPerPage = 50
  /**
   * ユーザがデータを更新 or 削除した際の最新のタイムスタンプ (ミリ秒)
   */
  userLatestStoreMethodActionTime: number | null = null
  userLatestStoreMethodActionModelName: string | null = null
  /**
   * @hidden
   */
  latestModelDefinitionLoadedTime: number | null = null
  /**
   * UI action 実行中の状態を保持, like "dragging-chart-items"
   */
  motion = ''
  /**
   * @hidden
   * CORE Front app 初期化済の際に、resolved になる Promise を保持。
   * レイアウトコンポーネント側で
   * await $core.$uiState.initLoadedPromise
   * とすることで、Frontの準備完了までawaitできる。
   */
  initLoadedPromise: Promise<any>
  /**
   * @hidden
   */
  __initLoadedResolveFunc: Function
  /**
   * HTML \<body\> タグに付与される css class(es) を配列で保持
   *
   * ```ts
   * // body に class を付与
   * $core.$uiState.bodyClasses.push('my-added-class')
   * ```
   */
  bodyClasses: string[] = []
  /**
   * PCサイズのみ: sidebar を開く/閉じるの制御が可能
   */
  isSidebarOpened = true
  /**
   * モバイルサイズのみ: sidebar を開く/閉じるの制御が可能
   */
  isSidebarActiveOnMobile = false

  /**
   *
   */
  isEnableHeader = true
  appRenderedAt: number = Date.now()

  /**
   * オンラインかどうかを保持
   */
  isOnline = ref(true)

  constructor() {
    this.initLoadedPromise = new Promise((resolve) => {
      this.__initLoadedResolveFunc = () => {
        resolve(true)
      }
    })
    this._initOfflineDetection()
  }

  /**
   * body class を返す
   * CORE/src/front/AppInitializer/CoreAppMain.vue にてwatchして制御している
   */
  get wrapperClasses() {
    return [
      this.wrapperClassesByUserRoles,
      this.bodyClasses.join(' '),
      this.isOnline.value === true ? 'is-online' : 'is-offline',
      this.appName ? `core-child-app core-app-name--${this.appName}` : 'core-app--admin',
    ].join(' ')
  }

  /**
   * 現在のアプリケーション定義名称を取得
   */
  get appName(): string {
    return $core.$appDefinitionLoader?.appDefinition?.name
  }

  private get wrapperClassesByUserRoles() {
    return (
      ($core.$embAuth.user?.isAdmin ? 'core-user-admin-role ' : '') +
      ($core.$embAuth.user?.coreRoles || [])
        .map((roleName) => `core-user-role--${roleName}`)
        .join(' ')
    )
  }

  /**
   * @hidden
   */
  static get instance(): UiState {
    return singletonInstanceSummoner('UiState', UiState)
  }

  private _initOfflineDetection() {
    if (typeof window === 'undefined') return // tmp fix for running syncModels with ts-node
    const events = ['online', 'offline', 'load']
    events.map((event) =>
      window.addEventListener(event, () => this._updateOnlineStatus()),
    )
  }

  private _updateOnlineStatus() {
    // if (typeof window === 'undefined') return // tmp fix for running syncModels with ts-node
    this.isOnline.value = window.navigator.onLine || false
    window.document.body.setAttribute(
      'data-is-online',
      this.isOnline.value ? 'true' : 'false',
    )
  }
}

export const $uiState = makeItReactive<UiState>(UiState.instance)
